import { Spinner } from "@/components/Spinner"
import { ApiError } from "@/lib/api"
import { formatCurrency } from "@/lib/utils"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { gerarBoleto } from "../api"
import type { BoletoGerado, Repasse } from "../schema"
import { Copiar, dataCompleta } from "./Copiar"
import { type Selecao, TabelaContratos } from "./TabelaContratos"
import { FOCO, levarAte } from "./foco"

const BOTAO = `min-h-11 rounded-full bg-brand px-6 py-2.5 font-display font-semibold text-brand-ink transition-colors hover:bg-brand-strong disabled:opacity-60 ${FOCO}`

const mensagemDe = (erro: unknown) =>
    erro instanceof ApiError && erro.mensagem
        ? erro.mensagem
        : "Não conseguimos gerar o boleto. Tente de novo em alguns minutos."

const Codigo = ({ titulo, valor }: { titulo: string; valor: string }) => (
    <div>
        <dt className="text-xs text-soft">{titulo}</dt>
        <dd className="mt-1 flex items-start gap-2">
            <code className="min-w-0 flex-1 break-all rounded bg-card px-2 py-1 font-mono text-xs select-all">
                {valor}
            </code>
            <Copiar valor={valor} rotulo={`Copiar ${titulo.toLowerCase()}`} />
        </dd>
    </div>
)

const ESPERA_ENTRE_LEITURAS = 3_000
const ESPERA_MAXIMA = 60_000

interface PainelProps {
    boleto: BoletoGerado
    esgotou: boolean
    onFechar: () => void
}

const PainelDoBoleto = ({ boleto, esgotou, onFechar }: PainelProps) => {
    const ref = useRef<HTMLElement>(null)
    useEffect(() => ref.current?.focus(), [])
    const { linhaDigitavel } = boleto
    return (
        <section
            ref={ref}
            tabIndex={-1}
            aria-labelledby="boleto-gerado"
            aria-busy={!linhaDigitavel && !esgotou}
            className="mb-4 rounded-lg border border-line bg-brand-wash px-5 py-4 outline-none"
        >
            <div className="flex items-start justify-between gap-4">
                <h4 id="boleto-gerado" className="font-semibold">
                    {linhaDigitavel ? "Boleto gerado" : "Gerando seu boleto…"}
                </h4>
                <button
                    type="button"
                    onClick={onFechar}
                    className={`text-sm text-soft underline ${FOCO}`}
                >
                    Fechar
                </button>
            </div>
            <dl className="mt-3 grid gap-3 text-sm">
                <div className="flex flex-wrap gap-x-8 gap-y-1">
                    <div>
                        <dt className="text-xs text-soft">Valor</dt>
                        <dd className="font-semibold tabular-nums">
                            {formatCurrency(boleto.valor)}
                        </dd>
                    </div>
                    <div>
                        <dt className="text-xs text-soft">Vencimento</dt>
                        <dd className="font-semibold">{dataCompleta(boleto.vencimento)}</dd>
                    </div>
                </div>
                {linhaDigitavel && <Codigo titulo="Linha digitável" valor={linhaDigitavel} />}
                {linhaDigitavel && boleto.pixCopiaECola && (
                    <Codigo titulo="PIX copia e cola" valor={boleto.pixCopiaECola} />
                )}
            </dl>
            {!linhaDigitavel && (
                <output className="mt-3 block text-sm text-soft">
                    {esgotou
                        ? "Seu boleto ainda está sendo gerado. Ele aparece na tabela abaixo em alguns minutos."
                        : "Leva alguns segundos. A linha digitável aparece aqui assim que o banco devolver."}
                </output>
            )}
            {linhaDigitavel && boleto.pdfUrl && (
                <a
                    href={boleto.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`mt-4 inline-block ${BOTAO}`}
                >
                    Baixar boleto (PDF)
                </a>
            )}
        </section>
    )
}

interface Props {
    repasse: Repasse
    caminho: string
    focar: boolean
}

export const NaoIdentificados = ({ repasse, caminho, focar }: Props) => {
    const queryClient = useQueryClient()
    const bloco = useRef<HTMLDivElement>(null)
    const gerar = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        if (focar) levarAte(bloco.current, gerar.current)
    }, [focar])
    // Guarda o que foi desmarcado: toda parcela cobrável nova já entra marcada.
    const [desmarcados, setDesmarcados] = useState<Set<string>>(new Set())
    const [gerado, setGerado] = useState<BoletoGerado | null>(null)
    const [esgotou, setEsgotou] = useState(false)

    const contratos = repasse.contratos.filter(
        (contrato) => contrato.situacao === "NAO_IDENTIFICADO",
    )
    const cobraveis = contratos.filter((contrato) => contrato.cobravel)
    const marcados = cobraveis.filter((contrato) => !desmarcados.has(contrato.id))
    const soma = marcados.reduce((total, contrato) => total + contrato.valor, 0)

    const daTabela = gerado
        ? repasse.contratos.find((contrato) => contrato.boleto?.id === gerado.id)?.boleto
        : undefined
    const boleto =
        gerado && !gerado.linhaDigitavel && daTabela?.linhaDigitavel
            ? {
                  ...gerado,
                  linhaDigitavel: daTabela.linhaDigitavel,
                  pixCopiaECola: daTabela.pixCopiaECola ?? gerado.pixCopiaECola,
                  pdfUrl: daTabela.pdfUrl ?? gerado.pdfUrl,
              }
            : gerado
    const aguardando = Boolean(boleto && !boleto.linhaDigitavel && !esgotou)

    useEffect(() => {
        if (!aguardando) return
        const leitura = setInterval(
            () => void queryClient.invalidateQueries({ queryKey: ["repasse", caminho] }),
            ESPERA_ENTRE_LEITURAS,
        )
        const limite = setTimeout(() => setEsgotou(true), ESPERA_MAXIMA)
        return () => {
            clearInterval(leitura)
            clearTimeout(limite)
        }
    }, [aguardando, queryClient, caminho])

    const mutation = useMutation({
        mutationFn: () =>
            gerarBoleto(
                repasse.id,
                marcados.map((contrato) => contrato.id),
            ),
        onSuccess: (novo) => {
            setGerado(novo)
            setEsgotou(false)
            void queryClient.invalidateQueries({ queryKey: ["repasse", caminho] })
        },
    })

    const alternar = (id: string) =>
        setDesmarcados((atual) => {
            const proximo = new Set(atual)
            if (!proximo.delete(id)) proximo.add(id)
            return proximo
        })

    const selecao: Selecao = {
        marcado: (contrato) => Boolean(contrato.cobravel) && !desmarcados.has(contrato.id),
        alternar: (contrato) => alternar(contrato.id),
        marcados: marcados.length,
        selecionaveis: cobraveis.length,
        alternarTodos: () =>
            setDesmarcados(
                marcados.length === cobraveis.length
                    ? new Set(cobraveis.map((contrato) => contrato.id))
                    : new Set(),
            ),
    }

    if (!contratos.length) return null

    return (
        <div ref={bloco}>
            <h3 className="mb-2 font-semibold">
                Não identificado{" "}
                <span className="font-normal text-faint">({contratos.length})</span>
            </h3>
            {cobraveis.length > 0 && (
                <p className="mb-3 rounded-lg bg-off-wash px-4 py-3 text-sm font-semibold text-off">
                    Quer evitar multas do Ministério do Trabalho (MTE), previstas na Lei
                    15.179/2025? Pague por aqui os não repasses que fazemos a baixa por você!
                    <span className="mt-1 block font-normal">
                        A lei prevê multa de 30% sobre o valor descontado e não repassado.
                    </span>
                </p>
            )}
            {boleto && (
                <PainelDoBoleto
                    boleto={boleto}
                    esgotou={esgotou}
                    onFechar={() => setGerado(null)}
                />
            )}
            <TabelaContratos
                contratos={contratos}
                selecao={cobraveis.length > 0 ? selecao : undefined}
            />
            {cobraveis.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center justify-end gap-4">
                    <p className="text-sm text-soft">
                        {marcados.length} {marcados.length === 1 ? "parcela" : "parcelas"} ·{" "}
                        <b className="tabular-nums text-ink">{formatCurrency(soma)}</b>
                    </p>
                    <button
                        ref={gerar}
                        type="button"
                        disabled={!marcados.length || mutation.isPending}
                        aria-busy={mutation.isPending}
                        onClick={() => mutation.mutate()}
                        className={`inline-flex items-center gap-2 ${BOTAO}`}
                    >
                        {mutation.isPending && <Spinner />}
                        Gerar boleto
                    </button>
                </div>
            )}
            {mutation.isError && (
                <p role="alert" className="mt-3 rounded-lg bg-off-wash px-4 py-3 text-sm text-off">
                    {mensagemDe(mutation.error)}
                </p>
            )}
        </div>
    )
}
